import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes, FaSearch, FaCheck, FaUpload, FaTrash } from 'react-icons/fa';
import calendarTaskService from '../../services/calendarTaskService';
import userService from '../../services/userService';
import { useFileUpload } from '../../hooks/useFileUpload';
import { formatFileSize } from '../../utils/uploadValidator';
import '../Projects/ProjectComposer.css';
import './CreateTaskModal.css';

// ─── Time Helpers ──────────────────────────────────────────────────────────────
const TIME_OPTIONS = (() => {
    const opts = [];
    for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 30) {
            const period = h < 12 ? 'AM' : 'PM';
            const displayH = h % 12 === 0 ? 12 : h % 12;
            const displayM = m === 0 ? '00' : '30';
            const label = `${displayH}:${displayM} ${period}`;
            const value = `${String(h).padStart(2, '0')}:${displayM}:00`;
            opts.push({ label, value });
        }
    }
    return opts;
})();

const PRIORITY_OPTIONS = [
    { value: 'low', label: 'Low' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
];

const STATUS_OPTIONS = [
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'tentative', label: 'Tentative' },
    { value: 'cancelled', label: 'Cancelled' },
];

const ALERT_OPTIONS = [
    { value: '', label: 'None' },
    { value: '0', label: 'At time of event' },
    { value: '5', label: '5 minutes before' },
    { value: '10', label: '10 minutes before' },
    { value: '15', label: '15 minutes before' },
    { value: '30', label: '30 minutes before' },
    { value: '60', label: '1 hour before' },
    { value: '1440', label: '1 day before' },
    { value: '10080', label: '1 week before' },
];

const OCCURRENCE_OPTIONS = [
    { value: 'none', label: 'Does not repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'annually', label: 'Annually' },
    { value: 'weekdays', label: 'Every weekday (Monday to Friday)' },
    { value: 'custom', label: 'Custom...' },
];

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const dateToString = (d) => {
    if (!d) return '';
    if (typeof d === 'string') return d.split('T')[0];
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// ─── Assignee Multi-Select ─────────────────────────────────────────────────────
const AssigneeSelect = ({ users, selectedIds, onToggle, error, loading }) => {
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const selected = users.filter(u => selectedIds.includes(String(u.id)));
    const filtered = users.filter(u =>
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="ctm-assignee-container">
            {selected.length > 0 && (
                <div className="ctm-assignee-chips">
                    {selected.map(u => (
                        <div key={u.id} className="ctm-chip">
                            <div className="ctm-chip-avatar">
                                {u.avatar_url
                                    ? <img src={u.avatar_url} alt={u.name} />
                                    : u.name?.charAt(0).toUpperCase()}
                            </div>
                            <span className="ctm-chip-name">{u.name}</span>
                            <button
                                type="button"
                                className="ctm-chip-remove"
                                onClick={() => onToggle(String(u.id))}
                                aria-label={`Remove ${u.name}`}
                            >
                                <FaTimes size={10} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <div className={`ctm-assignee-search-row ${error ? 'ctm-field-error-border' : ''}`}>
                <FaSearch className="ctm-search-icon" />
                <input
                    type="text"
                    className="ctm-assignee-input"
                    placeholder="Search members..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onFocus={() => setIsOpen(true)}
                    onBlur={() => setTimeout(() => setIsOpen(false), 150)}
                />
            </div>
            {isOpen && (
                <div className="ctm-assignee-dropdown">
                    {loading ? (
                        <div className="ctm-assignee-empty">Loading...</div>
                    ) : filtered.length === 0 ? (
                        <div className="ctm-assignee-empty">No results found</div>
                    ) : filtered.map(u => (
                        <div
                            key={u.id}
                            className={`ctm-assignee-item ${selectedIds.includes(String(u.id)) ? 'ctm-assignee-item--selected' : ''}`}
                            onMouseDown={() => onToggle(String(u.id))}
                        >
                            <div className="ctm-assignee-item-avatar">
                                {u.avatar_url
                                    ? <img src={u.avatar_url} alt={u.name} />
                                    : u.name?.charAt(0).toUpperCase()}
                            </div>
                            <div className="ctm-assignee-item-info">
                                <div className="ctm-assignee-item-name">{u.name}</div>
                                <div className="ctm-assignee-item-email">{u.email}</div>
                            </div>
                            {selectedIds.includes(String(u.id)) && (
                                <FaCheck className="ctm-assignee-check" />
                            )}
                        </div>
                    ))}
                </div>
            )}
            {error && <div className="ctm-field-error">{error}</div>}
        </div>
    );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const CreateTaskModal = ({ isOpen, onClose, onSuccess, task = null, defaultDate = null }) => {
    const isEdit = Boolean(task);

    // Form state
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [selectedAssigneeIds, setSelectedAssigneeIds] = useState([]);
    const [priority, setPriority] = useState('normal');
    const [status, setStatus] = useState('confirmed');
    const [startDate, setStartDate] = useState(today());
    const [endDate, setEndDate] = useState(today());
    const [startTime, setStartTime] = useState('09:00:00');
    const [endTime, setEndTime] = useState('10:00:00');
    const [allDay, setAllDay] = useState(false);
    const [occurrence, setOccurrence] = useState('none');
    const [customDays, setCustomDays] = useState([]);
    const [recurrenceEndType, setRecurrenceEndType] = useState('until');
    const [recurrenceUntil, setRecurrenceUntil] = useState('');
    const [recurrenceCount, setRecurrenceCount] = useState('');
    const [alert, setAlert] = useState('');

    // Users
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [formReady, setFormReady] = useState(false);

    // Submission
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    // File upload
    const { uploadedFiles, uploadFiles, removeFile, getUploadIds, clearAll } = useFileUpload();
    const fileInputRef = useRef(null);

    // ── Load users ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isOpen) { setFormReady(false); return; }
        setFormReady(false);
        setUsersLoading(true);
        userService.list()
            .then(res => {
                let data = res?.data;
                if (data && data.data) data = data.data;
                setUsers(Array.isArray(data) ? data : (data?.users ?? []));
            })
            .catch(() => setUsers([]))
            .finally(() => { setUsersLoading(false); setFormReady(true); });
    }, [isOpen]);

    // ── Populate form in edit mode ──────────────────────────────────────────────
    useEffect(() => {
        if (!isOpen) return;
        if (isEdit && task) {
            setErrors({});
            setTitle(task.title || '');
            setDescription(task.description || '');
            setSelectedAssigneeIds((task.assignees || []).map(u => String(u.id)));
            setPriority(task.priority || 'normal');
            setStatus(task.status || 'confirmed');
            setStartDate(dateToString(task.start_at));
            setEndDate(dateToString(task.end_at));
            setAllDay(Boolean(task.all_day));
            if (task.start_at && !task.all_day) {
                const t = task.start_at.split('T')[1];
                if (t) setStartTime(t.substring(0, 8));
            }
            if (task.end_at && !task.all_day) {
                const t = task.end_at.split('T')[1];
                if (t) setEndTime(t.substring(0, 8));
            }
            setAlert(task.reminder_minutes != null ? String(task.reminder_minutes) : '');
            if (task.recurrence && task.recurrence.frequency && task.recurrence.frequency !== 'once') {
                const f = task.recurrence.frequency;
                if (f === 'daily') setOccurrence('daily');
                else if (f === 'weekly') {
                    const days = task.recurrence.days_of_week || [];
                    const isWeekdays = days.length === 5 && !days.includes('Sat') && !days.includes('Sun');
                    setOccurrence(isWeekdays ? 'weekdays' : 'weekly');
                    setCustomDays(days);
                } else if (f === 'monthly') setOccurrence('monthly');
                else if (f === 'yearly') setOccurrence('annually');
                else { setOccurrence('custom'); setCustomDays(task.recurrence.days_of_week || []); }

                if (task.recurrence.recurrence_end_date) {
                    setRecurrenceEndType('until');
                    setRecurrenceUntil(dateToString(task.recurrence.recurrence_end_date));
                } else if (task.recurrence.occurrences) {
                    setRecurrenceEndType('count');
                    setRecurrenceCount(String(task.recurrence.occurrences));
                }
            } else {
                setOccurrence('none');
            }
        } else {
            // Create mode defaults
            setTitle('');
            setDescription('');
            setSelectedAssigneeIds([]);
            setPriority('normal');
            setStatus('confirmed');
            const d = defaultDate ? dateToString(defaultDate) : today();
            setStartDate(d);
            setEndDate(d);
            setStartTime('09:00:00');
            setEndTime('10:00:00');
            setAllDay(false);
            setOccurrence('none');
            setCustomDays([]);
            setRecurrenceEndType('until');
            setRecurrenceUntil('');
            setRecurrenceCount('');
            setAlert('');
            setErrors({});
            clearAll();
        }
    }, [isOpen, isEdit, task, defaultDate]);

    const toggleAssignee = useCallback((id) => {
        setSelectedAssigneeIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    }, []);

    const toggleCustomDay = (day) => {
        setCustomDays(prev =>
            prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
        );
    };

    const buildRecurrence = () => {
        if (occurrence === 'none') return null;
        const base = {
            interval: 1,
            days_of_week: [],
            recurrence_end_date: recurrenceEndType === 'until' && recurrenceUntil ? recurrenceUntil : null,
            occurrences: recurrenceEndType === 'count' && recurrenceCount ? parseInt(recurrenceCount, 10) : null,
        };
        if (occurrence === 'daily') return { ...base, frequency: 'daily' };
        if (occurrence === 'weekly') return { ...base, frequency: 'weekly' };
        if (occurrence === 'monthly') return { ...base, frequency: 'monthly' };
        if (occurrence === 'annually') return { ...base, frequency: 'yearly' };
        if (occurrence === 'weekdays') return { ...base, frequency: 'weekly', days_of_week: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] };
        if (occurrence === 'custom') return { ...base, frequency: 'weekly', days_of_week: customDays };
        return null;
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            uploadFiles(e.target.files);
            e.target.value = '';
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setErrors({});

        const data = {
            title,
            description,
            assignee_ids: selectedAssigneeIds.map(id => parseInt(id, 10)),
            priority,
            status,
            start_at: allDay ? `${startDate}T00:00:00` : `${startDate}T${startTime}`,
            end_at: allDay ? `${endDate}T23:59:59` : `${endDate}T${endTime}`,
            all_day: allDay,
            reminder_minutes: alert !== '' ? parseInt(alert, 10) : null,
            recurrence: occurrence !== 'none' ? buildRecurrence() : null,
            attachment_ids: getUploadIds(),
        };

        try {
            const response = isEdit
                ? await calendarTaskService.updateTask(task.id, data)
                : await calendarTaskService.createTask(data);

            if (response && response.status === 422) {
                setErrors(response.errors || {});
                return;
            }
            if (onSuccess) onSuccess(response?.data ?? response);
            onClose();
        } catch (e) {
            if (e && e.status === 422) {
                setErrors(e.errors || {});
            }
            // interceptor already toasted for other errors
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    if (!isOpen) return null;

    const hasRecurrence = occurrence !== 'none';

    const modalContent = (
        <div className="project-composer-overlay ctm-overlay" onClick={handleBackdropClick}>
            <div className="project-composer-modal ctm-modal">
                {/* Header */}
                <div className="project-composer-header">
                    <h2 className="project-composer-title">{isEdit ? 'Edit Task' : 'Create Task'}</h2>
                    <button className="project-composer-close" onClick={onClose} type="button">
                        <FaTimes size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="project-composer-body ctm-body">
                {!formReady ? (
                    <div className="ctm-skeleton-body">
                        <div className="ctm-skeleton ctm-skeleton-input" />
                        <div className="ctm-skeleton ctm-skeleton-textarea" />
                        <div className="ctm-skeleton ctm-skeleton-input" />
                        <div className="ctm-skeleton ctm-skeleton-input" />
                        <div className="ctm-skeleton-row-2">
                            <div className="ctm-skeleton ctm-skeleton-select" />
                            <div className="ctm-skeleton ctm-skeleton-select" />
                        </div>
                        <div className="ctm-skeleton-row-3">
                            <div className="ctm-skeleton ctm-skeleton-select" />
                            <div className="ctm-skeleton ctm-skeleton-select" />
                            <div className="ctm-skeleton ctm-skeleton-select" />
                        </div>
                        <div className="ctm-skeleton-row-3">
                            <div className="ctm-skeleton ctm-skeleton-select" />
                            <div className="ctm-skeleton ctm-skeleton-select" />
                            <div className="ctm-skeleton ctm-skeleton-select ctm-skeleton-select-sm" />
                            <div className="ctm-skeleton ctm-skeleton-toggle" />
                        </div>
                    </div>
                ) : (
                <>{/* Row 1: Title */}
                    <div className="ctm-field-group ctm-full-width">
                        <label className="ctm-label">
                            Task Name <span className="ctm-required">*</span>
                        </label>
                        <input
                            type="text"
                            className={`ctm-input ${errors.title ? 'ctm-input--error' : ''}`}
                            placeholder="Task name..."
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                        />
                        {errors.title && <div className="ctm-field-error">{errors.title[0]}</div>}
                    </div>

                    {/* Row 2: Description */}
                    <div className="ctm-field-group ctm-full-width">
                        <label className="ctm-label">Task Description</label>
                        <textarea
                            className={`ctm-textarea ${errors.description ? 'ctm-input--error' : ''}`}
                            placeholder="Description..."
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            rows={3}
                        />
                        {errors.description && <div className="ctm-field-error">{errors.description[0]}</div>}
                    </div>

                    {/* Row 3: Assign a Member (full width) */}
                    <div className="ctm-field-group">
                        <label className="ctm-label">
                            Assign a Member <span className="ctm-required">*</span>
                        </label>
                        <AssigneeSelect
                            users={users}
                            selectedIds={selectedAssigneeIds}
                            onToggle={toggleAssignee}
                            error={errors.assignee_ids ? errors.assignee_ids[0] : null}
                            loading={usersLoading}
                        />
                    </div>

                    {/* Row 4: Attachment (full width) */}
                    <div className="ctm-field-group">
                        <label className="ctm-label">Attachment</label>
                        <div
                            className="ctm-upload-area"
                            onClick={() => fileInputRef.current?.click()}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => e.key === 'Enter' && fileInputRef.current?.click()}
                        >
                            <svg className="ctm-upload-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5.00065 18.3346C4.55862 18.3346 4.1347 18.159 3.82214 17.8465C3.50958 17.5339 3.33398 17.11 3.33398 16.668V3.33464C3.33398 2.89261 3.50958 2.46869 3.82214 2.15613C4.1347 1.84357 4.55862 1.66797 5.00065 1.66797H11.6673C11.9311 1.66754 12.1924 1.71931 12.4361 1.82028C12.6798 1.92125 12.9011 2.06944 13.0873 2.2563L16.0773 5.2463C16.2647 5.43256 16.4133 5.65409 16.5146 5.89811C16.6158 6.14212 16.6677 6.40378 16.6673 6.66797V16.668C16.6673 17.11 16.4917 17.5339 16.1792 17.8465C15.8666 18.159 15.4427 18.3346 15.0007 18.3346H5.00065Z" stroke="#414141" strokeOpacity="0.85" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M11.666 1.66797V5.83464C11.666 6.05565 11.7538 6.26761 11.9101 6.42389C12.0664 6.58017 12.2783 6.66797 12.4993 6.66797H16.666" stroke="#414141" strokeOpacity="0.85" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M10 10V15" stroke="#414141" strokeOpacity="0.85" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M12.5 12.5L10 10L7.5 12.5" stroke="#414141" strokeOpacity="0.85" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span className="ctm-upload-text">
                                {uploadedFiles.length === 0
                                    ? 'Upload Document'
                                    : uploadedFiles.length === 1
                                        ? uploadedFiles[0].name
                                        : `${uploadedFiles.length} files selected`}
                            </span>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            className="ctm-file-input-hidden"
                            onChange={handleFileChange}
                        />
                        {uploadedFiles.length > 0 && (
                            <div className="ctm-file-list">
                                {uploadedFiles.map(f => (
                                    <div key={f.id} className={`ctm-file-item ctm-file-item--${f.status}`}>
                                        <span className="ctm-file-name">{f.name}</span>
                                        <span className="ctm-file-size">{formatFileSize(f.size)}</span>
                                        {f.status === 'uploading' && (
                                            <span className="ctm-file-status">Uploading...</span>
                                        )}
                                        {f.status === 'error' && (
                                            <span className="ctm-file-status ctm-file-status--error">{f.error}</span>
                                        )}
                                        <button
                                            type="button"
                                            className="ctm-file-remove"
                                            onClick={e => { e.stopPropagation(); removeFile(f.id, f.uploadId); }}
                                            aria-label="Remove file"
                                        >
                                            <FaTrash size={11} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Row 4: Priority + Status */}
                    <div className="ctm-row-2col">
                        <div className="ctm-field-group">
                            <label className="ctm-label">
                                Priority <span className="ctm-required">*</span>
                            </label>
                            <select
                                className={`ctm-select ${errors.priority ? 'ctm-input--error' : ''}`}
                                value={priority}
                                onChange={e => setPriority(e.target.value)}
                            >
                                {PRIORITY_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                            {errors.priority && <div className="ctm-field-error">{errors.priority[0]}</div>}
                        </div>
                        <div className="ctm-field-group">
                            <label className="ctm-label">
                                Status <span className="ctm-required">*</span>
                            </label>
                            <select
                                className={`ctm-select ${errors.status ? 'ctm-input--error' : ''}`}
                                value={status}
                                onChange={e => setStatus(e.target.value)}
                            >
                                {STATUS_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                            {errors.status && <div className="ctm-field-error">{errors.status[0]}</div>}
                        </div>
                    </div>

                    {/* Row 5: Start Date + End Date + Occurrence */}
                    <div className="ctm-row-3col">
                        <div className="ctm-field-group">
                            <label className="ctm-label">
                                Start Date <span className="ctm-required">*</span>
                            </label>
                            <input
                                type="date"
                                className={`ctm-input ${errors.start_at ? 'ctm-input--error' : ''}`}
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                            />
                            {errors.start_at && <div className="ctm-field-error">{errors.start_at[0]}</div>}
                        </div>
                        <div className="ctm-field-group">
                            <label className="ctm-label">
                                End Date <span className="ctm-required">*</span>
                            </label>
                            <input
                                type="date"
                                className={`ctm-input ${errors.end_at ? 'ctm-input--error' : ''}`}
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                            />
                            {errors.end_at && <div className="ctm-field-error">{errors.end_at[0]}</div>}
                        </div>
                        <div className="ctm-field-group">
                            <label className="ctm-label">Occurrence</label>
                            <select
                                className="ctm-select"
                                value={occurrence}
                                onChange={e => { setOccurrence(e.target.value); setCustomDays([]); setRecurrenceEndType('until'); setRecurrenceUntil(''); setRecurrenceCount(''); }}
                            >
                                {OCCURRENCE_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Row 6: Start Time + End Time (hidden when all day) + Alert + All Day */}
                    <div className="ctm-row-time">
                        {!allDay && (
                            <>
                                <div className="ctm-field-group">
                                    <label className="ctm-label">Start Time</label>
                                    <select
                                        className="ctm-select"
                                        value={startTime}
                                        onChange={e => setStartTime(e.target.value)}
                                    >
                                        {TIME_OPTIONS.map(o => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="ctm-field-group">
                                    <label className="ctm-label">End Time</label>
                                    <select
                                        className="ctm-select"
                                        value={endTime}
                                        onChange={e => setEndTime(e.target.value)}
                                    >
                                        {TIME_OPTIONS.map(o => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </>
                        )}
                        <div className="ctm-field-group">
                            <label className="ctm-label">Alert</label>
                            <select
                                className="ctm-select"
                                value={alert}
                                onChange={e => setAlert(e.target.value)}
                            >
                                {ALERT_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="ctm-field-group ctm-allday-group">
                            <label className="ctm-label">&nbsp;</label>
                            <div className="ctm-allday-row">
                                <button
                                    type="button"
                                    className={`ctm-toggle ${allDay ? 'ctm-toggle--on' : ''}`}
                                    onClick={() => setAllDay(v => !v)}
                                    role="switch"
                                    aria-checked={allDay}
                                >
                                    <span className="ctm-toggle-thumb" />
                                </button>
                                <span className="ctm-allday-label">All Day</span>
                            </div>
                        </div>
                    </div>

                    {/* Recurrence sub-section */}
                    {hasRecurrence && (
                        <div className="ctm-recurrence-section">
                            {occurrence === 'custom' && (
                                <div className="ctm-field-group ctm-full-width">
                                    <label className="ctm-label">Repeat on</label>
                                    <div className="ctm-days-row">
                                        {DAYS_OF_WEEK.map(day => (
                                            <button
                                                key={day}
                                                type="button"
                                                className={`ctm-day-btn ${customDays.includes(day) ? 'ctm-day-btn--active' : ''}`}
                                                onClick={() => toggleCustomDay(day)}
                                            >
                                                {day}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="ctm-recurrence-end">
                                <label className="ctm-label">End recurrence</label>
                                <div className="ctm-radio-row">
                                    <label className="ctm-radio-label">
                                        <input
                                            type="radio"
                                            name="recurrence_end"
                                            value="until"
                                            checked={recurrenceEndType === 'until'}
                                            onChange={() => setRecurrenceEndType('until')}
                                        />
                                        Until date
                                    </label>
                                    <label className="ctm-radio-label">
                                        <input
                                            type="radio"
                                            name="recurrence_end"
                                            value="count"
                                            checked={recurrenceEndType === 'count'}
                                            onChange={() => setRecurrenceEndType('count')}
                                        />
                                        Number of occurrences
                                    </label>
                                </div>
                                {recurrenceEndType === 'until' && (
                                    <input
                                        type="date"
                                        className="ctm-input ctm-recurrence-date"
                                        value={recurrenceUntil}
                                        onChange={e => setRecurrenceUntil(e.target.value)}
                                    />
                                )}
                                {recurrenceEndType === 'count' && (
                                    <input
                                        type="number"
                                        className="ctm-input ctm-recurrence-count"
                                        min="1"
                                        placeholder="e.g. 10"
                                        value={recurrenceCount}
                                        onChange={e => setRecurrenceCount(e.target.value)}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </>)}
                </div>

                {/* Footer */}
                <div className="ctm-footer">
                    <button
                        type="button"
                        className="ctm-btn-cancel"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="ctm-btn-save"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modalContent, document.body);
};

export default CreateTaskModal;
